import React, { useState } from 'react';
import { formatTime, linkifyText, linkifyTextCommunity } from '../utils/utils';
import AudioPlayer from './AudioPlayer';
import MessageReactions from './MessageReactions';
import { QuotedMessage } from './ReplyQuotedMessage';
import { MessageStatus, GroupMessageStatus } from './MessageStatus';
import { FiVideo } from 'react-icons/fi';
import { BsTelephone } from 'react-icons/bs';
import { formatDuration } from '../utils/chatUtils';
import { IMAGES } from '../constants/images';
import VideoModal from './VideoModal';
import { ROUTES } from '../App';
import useMoveTo from '../constants/navigation';
import ForwardedMessage from './ForwardedMessage';
import ViewUsersModal from './ViewUsersModal';

const MessageBubble = ({
    message,
    isFromCurrentUser,
    currentUserId,
    senderName,
    senderAvatar,
    showHeader,
    onAvatarClick,
    onImageClick,
    selectionMode,
    selected,
    onToggleReaction,
    onReply,
    onEdit,
    onDelete,
    showReplyButton = false,
    isHost = false,
    chatType = 'private',
    totalMembers,
    loadedImages,
    onImageLoad,
    renderCustomContent
}) => {
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [videoThumbnailLoaded, setVideoThumbnailLoaded] = useState({});
    const [videoThumbnails, setVideoThumbnails] = useState({});
    const [perImageLoaded, setPerImageLoaded] = useState({});
    const [isExpanded, setIsExpanded] = useState(false);
    const [viewersModalOpen, setViewersModalOpen] = useState(false);
    const [viewersList, setViewersList] = useState([]);
    const navigate = useMoveTo();

    const TEXT_LIMIT = 250;
    const isLongText = message.text && message.text.length > TEXT_LIMIT;
    const displayText = isLongText && !isExpanded ? message.text.slice(0, TEXT_LIMIT) + "..." : message.text;

    const shouldShowHeader =
        showHeader;

    const shouldShowMissedCallInGroup =
        chatType === 'group' &&
        message.type === 'call' &&
        message.callStatus === 'missed' &&
        message.missedReceiver &&
        message.missedReceiver[currentUserId];

    const shouldRenderCallMessage =
        message.type === 'call' &&
        message.callStatus === 'missed' &&
        (
            chatType === 'private' ||
            shouldShowMissedCallInGroup
        );

    const renderMessageStatus = () => {
        if (chatType === 'private') {
            return <MessageStatus message={message} isFromCurrentUser={isFromCurrentUser} />;
        } else {
            return (
                <GroupMessageStatus
                    message={message}
                    isFromCurrentUser={isFromCurrentUser}
                    totalMembers={totalMembers}
                    onViewDetails={handleViewDetails}
                />
            );
        }
    };

    const handleViewDetails = (msg) => {
        const viewers = msg.seenBy || msg.isSeen || {};
        const uids = Object.keys(viewers).filter(uid => uid !== currentUserId);
        setViewersList(uids);
        setViewersModalOpen(true);
    };

    // Define getVideoThumbnail before it's used
    const getVideoThumbnail = (videoUrl, messageId) => {
        // If we already have a thumbnail for this video, return it
        if (videoThumbnails[messageId]) {
            return videoThumbnails[messageId];
        }

        // If the message already has a thumbnail from the server, use it
        if (message.videoThumbnail) {
            return message.videoThumbnail;
        }

        // Otherwise, generate a thumbnail on the client side
        const video = document.createElement('video');
        video.src = videoUrl;
        video.crossOrigin = 'anonymous';
        video.preload = 'metadata';

        video.addEventListener('loadeddata', () => {
            video.currentTime = 1; // Seek to 1 second
        });

        video.addEventListener('seeked', () => {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const thumbnail = canvas.toDataURL('image/jpeg');

            // Store the generated thumbnail
            setVideoThumbnails(prev => ({ ...prev, [messageId]: thumbnail }));

            // Mark as loaded
            handleVideoThumbnailLoad(messageId);
        });

        video.addEventListener('error', () => {
            // Use fallback image on error
            setVideoThumbnails(prev => ({ ...prev, [messageId]: IMAGES.statues }));
            handleVideoThumbnailLoad(messageId);
        });

        // Return a placeholder immediately, the actual thumbnail will be set when generated
        return videoThumbnails[messageId] || null;
    };


    const handleVideoClick = (videoUrl) => {
        setSelectedVideo(videoUrl);
    };

    const handleVideoThumbnailLoad = (id) => {
        setVideoThumbnailLoaded(prev => ({ ...prev, [id]: true }));
    };

    const mediaImages = Array.isArray(message.imageUrls)
        ? message.imageUrls
        : Array.isArray(message.images)
            ? message.images
            : message.imageUrl
                ? [message.imageUrl]
                : [];
    const hasForwardSource = Boolean(message.originalGroup || message.originalCommunity);
    const forwardedType = message.originalCommunity ? "community" : "group";

    return (
        <div className={`flex items-start gap-1.5 ${showHeader ? 'mt-4' : 'mt-1'} ${isFromCurrentUser ? "justify-end" : "justify-start"} w-full px-2`}>
            {/* Avatar - WhatsApp style (only for incoming and only on header) */}
            {!isFromCurrentUser && (
                <div className="w-8 flex-shrink-0">
                    {shouldShowHeader && (
                        <button onClick={onAvatarClick}>
                            <img
                                src={senderAvatar}
                                alt={senderName}
                                className="w-8 h-8 rounded-full cursor-pointer flex-shrink-0 object-cover"
                            />
                        </button>
                    )}
                </div>
            )}

            {/* Message Content Container */}
            <div className={`relative flex flex-col md:max-w-[70%] max-w-[85%] ${isFromCurrentUser ? "items-end" : "items-start"} ${message.reactions && Object.keys(message.reactions).length > 0 ? "mb-3" : ""}`}>
                
                {/* Sender Name (WhatsApp Group style) */}
                {!isFromCurrentUser && shouldShowHeader && chatType !== 'private' && (
                    <button onClick={onAvatarClick} className="ml-1 mb-0.5">
                        <p className={`font-semibold text-[13px] cursor-pointer ${message.isHost ? "text-[#327691]" : "text-primary"}`}>
                            {senderName}
                        </p>
                    </button>
                )}

                {/* Forwarded message */}
                {message.type === 'forwarded' && hasForwardSource && (
                    <ForwardedMessage
                        message={message}
                        isFromCurrentUser={isFromCurrentUser}
                        type={forwardedType}
                        selectionMode={selectionMode}
                        selected={selected}
                        onToggleReaction={onToggleReaction}
                    />
                )}

                {message.type === 'forwarded' && !hasForwardSource && (
                    <div className="flex items-center gap-1 mb-1 ml-2 opacity-70 italic text-[11px]">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M10 9V5l7 7-7 7v-4.1c-5 0-8.5 1.6-11 5.1 1-5 4-10 11-11z"/></svg>
                        Shared
                    </div>
                )}

                {/* Main Bubble */}
                {message.text && (message.type !== 'forwarded' || !hasForwardSource) && (
                    <div
                        className={`relative px-2.5 py-1.5 shadow-sm ${
                            isFromCurrentUser
                                ? "bg-primary text-white rounded-lg rounded-tr-none"
                                : "bg-[#ffffff] dark:bg-[#202c33] text-[#111b21] dark:text-[#e9edef] rounded-lg rounded-tl-none"
                        } ${selectionMode && selected ? "ring-2 ring-primary ring-offset-1" : ""}`}
                        style={{
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                        }}
                    >
                        {message.type === 'forwarded' && !hasForwardSource && (
                            <div className="flex items-center gap-1 mb-1 text-[11px] opacity-70">
                                <span className={`font-medium ${isFromCurrentUser ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                                    Forwarded message
                                </span>
                            </div>
                        )}

                        {/* WhatsApp Tail (Only on header) */}
                        {shouldShowHeader && (
                            <div className={`absolute top-0 w-3 h-4 ${
                                isFromCurrentUser 
                                    ? "-right-2 bg-primary" 
                                    : "-left-2 bg-[#ffffff] dark:bg-[#202c33]"
                                }`} 
                                style={{
                                    clipPath: isFromCurrentUser 
                                        ? "polygon(0 0, 0 100%, 100% 0)" 
                                        : "polygon(100% 0, 100% 100%, 0 0)"
                                }}
                            />
                        )}

                        {/* Reply Quote — inside bubble */}
                        {message.replyTo && chatType !== 'community' && (
                            <div className="mb-2">
                                <QuotedMessage
                                    replyTo={message.replyTo}
                                    isFromCurrentUser={isFromCurrentUser}
                                    currentUserId={currentUserId}
                                    senderName={message.replyTo.senderId === currentUserId ? "You" : (message.replyTo.senderName || senderName)}
                                />
                            </div>
                        )}

                        <div className="w-full">
                            <p className="text-[15px] leading-[20px] whitespace-pre-wrap">
                                {chatType === "community" ?
                                    linkifyTextCommunity(
                                        displayText,
                                        message.mentions,
                                        isFromCurrentUser,
                                        (name, uid) => navigate(`${ROUTES.VIEW_PROFILE}/${uid}`)
                                    ) : linkifyText(displayText, isFromCurrentUser)
                                }
                            </p>
                            {isLongText && (
                                <button 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsExpanded(!isExpanded);
                                    }} 
                                    className={`${isFromCurrentUser ? "text-white" : "text-primary dark:text-[#57a1be]"} text-[13px] font-semibold mt-1 hover:underline`}
                                >
                                    {isExpanded ? "Read less" : "Read more"}
                                </button>
                            )}
                        </div>

                        
                        <div className="flex items-center justify-between gap-3 mt-1 w-full clear-both">
                            {chatType === 'community' ? (
                                <button 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (onReply) onReply(message);
                                    }}
                                    className="flex items-center gap-1 text-[11px] opacity-70 hover:opacity-100 transition-opacity"
                                >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                                    <span>{message.commentCount || 0}</span>
                                </button>
                            ) : <div />}
                            <div className="flex items-center gap-1">
                                {message.edited && (
                                    <span className="text-[10px] opacity-60 italic">edited</span>
                                )}
                                <p className="text-[11px] opacity-60 whitespace-nowrap">
                                    {formatTime(message.timestamp)}
                                </p>
                                {renderMessageStatus()}
                            </div>
                        </div>
                    </div>
                )}

                {/* Image Message */}
                {mediaImages.length > 0 && (
                    <div
                        className={`relative mt-2 cursor-pointer max-w-full ${isFromCurrentUser ? "ml-auto" : "mr-auto"}`}
                        style={{ touchAction: "pan-y" }}
                    >
                        <div
                            className={`
                                relative
                                max-w-[250px] sm:max-w-[280px] md:max-w-[320px]
                                rounded-xl sm:rounded-2xl border-2
                                ${selectionMode && selected ? "border-primary" : isFromCurrentUser ? "border-primary" : "border-gray-200 dark:border-gray-700"}
                                overflow-hidden ${isFromCurrentUser ? "bg-primary" : "bg-white dark:bg-[#202c33]"} p-1
                                ${isFromCurrentUser ? "ml-auto" : "mr-auto"}
                                shadow-sm
                            `}
                        >
                            {message.replyTo && chatType !== 'community' && (
                                <div className="px-1 pt-1 pb-1">
                                    <QuotedMessage
                                        replyTo={message.replyTo}
                                        isFromCurrentUser={isFromCurrentUser}
                                        currentUserId={currentUserId}
                                        senderName={message.replyTo.senderId === currentUserId ? "You" : (message.replyTo.senderName || senderName)}
                                    />
                                </div>
                            )}
                            <div className={`${mediaImages.length === 1
                                    ? "grid grid-cols-1"
                                    : mediaImages.length === 2
                                        ? "grid grid-cols-2 gap-1"
                                        : "grid grid-cols-2 gap-1"
                                }`}>
                                {mediaImages.slice(0, 4).map((imgSrc, index) => (
                                    <div
                                        key={`${message.id}-img-${index}`}
                                        className={`relative overflow-hidden rounded-lg bg-gray-300 dark:bg-gray-700 ${mediaImages.length === 3 && index === 0 ? "col-span-2" : ""}`}
                                        onClick={onImageClick}
                                    >
                                        {!perImageLoaded[index] && (
                                            <div className="absolute inset-0 animate-pulse bg-gray-300 dark:bg-gray-700" />
                                        )}
                                        <img
                                            src={imgSrc}
                                            alt="Uploaded content"
                                            loading="lazy"
                                            decoding="async"
                                            onLoad={() => {
                                                setPerImageLoaded(prev => ({ ...prev, [index]: true }));
                                                onImageLoad?.();
                                            }}
                                            onError={() => {
                                                setPerImageLoaded(prev => ({ ...prev, [index]: true }));
                                                onImageLoad?.();
                                            }}
                                            className={`w-full h-auto max-h-[360px] object-cover block transition-opacity duration-300 ${perImageLoaded[index] ? "opacity-100" : "opacity-0"}`}
                                        />
                                        {mediaImages.length > 4 && index === 3 && (
                                            <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                                                <span className="text-white text-sm font-semibold">+{mediaImages.length - 4}</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="flex items-center justify-between mt-1 px-1 gap-2">
                                {chatType === 'community' ? (
                                    <button 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (onReply) onReply(message);
                                        }}
                                        className={`flex items-center gap-1 text-[11px] opacity-70 hover:opacity-100 transition-opacity ${isFromCurrentUser ? "text-gray-200" : "text-gray-500"}`}
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                                        <span>{message.commentCount || 0}</span>
                                    </button>
                                ) : <div />}
                                
                                <div className="flex items-center gap-1">
                                    <span className={`text-[10px] ${isFromCurrentUser ? "text-gray-200" : "text-gray-500"}`}>{formatTime(message.timestamp)}</span>
                                    {isFromCurrentUser && renderMessageStatus()}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Video message with thumbnail and play button */}
                {message.videoUrl && (
                    <div className={`relative mt-1 sm:mt-2 max-w-[320px] md:max-w-[360px] ${isFromCurrentUser ? "ml-auto" : "mr-auto"} `}>
                        <div
                            className={`
                                relative rounded-xl sm:rounded-2xl border-2
                                ${selectionMode && selected ? "border-primary" : isFromCurrentUser ? "border-primary" : "border-gray-200 dark:border-gray-700"}
                                overflow-hidden ${isFromCurrentUser ? "bg-primary" : "bg-white dark:bg-[#202c33]"} p-1
                                shadow-sm
                            `}
                        >
                            <div 
                                className="relative rounded-lg overflow-hidden cursor-pointer group aspect-video"
                                onClick={() => handleVideoClick(message.videoUrl)}
                            >
                                {/* Video thumbnail */}
                                <div className="relative w-full h-full bg-gray-200 dark:bg-[#2A2A2A]">                                {/* Loading shimmer */}
                                    {!videoThumbnailLoaded[message.id] && (
                                        <div className="absolute inset-0 animate-pulse bg-gray-300 dark:bg-gray-700 rounded-xl" />
                                    )}

                                    {/* Thumbnail image */}
                                    <img
                                        src={getVideoThumbnail(message.videoUrl, message.id)}
                                        alt="Video thumbnail"
                                        loading="lazy"
                                        decoding="async"
                                        onLoad={() => handleVideoThumbnailLoad(message.id)}
                                        onError={(e) => {
                                            e.target.src = IMAGES.videoPlaceHolder;
                                            handleVideoThumbnailLoad(message.id);
                                        }}
                                        className={`
                                            w-full h-full object-cover transition-opacity duration-300
                                            ${videoThumbnailLoaded[message.id] ? "opacity-100" : "opacity-0"}
                                        `}
                                    />

                                    {/* Play button overlay */}
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                                        <div className="w-12 h-12 rounded-full bg-white/70 flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                                            <svg
                                                className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-primary ml-1"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between mt-1 px-1 gap-2">
                                {chatType === 'community' ? (
                                    <button 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (onReply) onReply(message);
                                        }}
                                        className={`flex items-center gap-1 text-[11px] opacity-70 hover:opacity-100 transition-opacity ${isFromCurrentUser ? "text-gray-200" : "text-gray-500"}`}
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                                        <span>{message.commentCount || 0}</span>
                                    </button>
                                ) : <div />}
                                
                                <div className="flex items-center gap-1">
                                    <span className={`text-[10px] ${isFromCurrentUser ? "text-gray-200" : "text-gray-500"}`}>{formatTime(message.timestamp)}</span>
                                    {isFromCurrentUser && renderMessageStatus()}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Audio Message */}
                {message.audioUrl && (
                    <div className={`relative mt-2 max-w-full ${isFromCurrentUser ? "ml-auto" : "mr-auto"}`}>
                        <div
                            className={`
                                relative max-w-[280px] sm:max-w-[320px] rounded-xl sm:rounded-2xl border-2
                                ${selectionMode && selected ? "border-primary" : isFromCurrentUser ? "border-primary" : "border-gray-200 dark:border-gray-700"}
                                overflow-hidden ${isFromCurrentUser ? "bg-primary" : "bg-white dark:bg-[#202c33]"} p-2
                                shadow-sm
                            `}
                        >
                            {message.replyTo && chatType !== 'community' && (
                                <div className="mb-2">
                                    <QuotedMessage
                                        replyTo={message.replyTo}
                                        isFromCurrentUser={isFromCurrentUser}
                                        currentUserId={currentUserId}
                                        senderName={message.replyTo.senderId === currentUserId ? "You" : (message.replyTo.senderName || senderName)}
                                    />
                                </div>
                            )}
                            <AudioPlayer
                                src={message.audioUrl}
                                isFromCurrentUser={isFromCurrentUser}
                            />
                            
                            <div className="flex items-center justify-between mt-1 px-1 gap-2">
                                {chatType === 'community' ? (
                                    <button 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (onReply) onReply(message);
                                        }}
                                        className={`flex items-center gap-1 text-[11px] opacity-70 hover:opacity-100 transition-opacity ${isFromCurrentUser ? "text-gray-200" : "text-gray-500"}`}
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                                        <span>{message.commentCount || 0}</span>
                                    </button>
                                ) : <div />}
                                
                                <div className="flex items-center gap-1">
                                    <span className={`text-[10px] ${isFromCurrentUser ? "text-gray-200" : "text-gray-500"}`}>{formatTime(message.timestamp)}</span>
                                    {isFromCurrentUser && renderMessageStatus()}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {shouldRenderCallMessage ? (
                    <div className={`flex ${isFromCurrentUser ? 'justify-end' : 'justify-start'} mb-2`}>
                        <div className={`max-w-[100%] rounded-lg p-3 ${isFromCurrentUser
                            ? 'bg-primary text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-black dark:text-white'
                            }`}>
                            <div className="flex items-center gap-2">
                                {/* Call icon based on type and status */}
                                {message.callType === 'video' ? (
                                    <FiVideo className="w-5 h-5" />
                                ) : (
                                    <BsTelephone className="w-5 h-5" />
                                )}

                                <div className="flex flex-col">
                                    <span className="font-medium">
                                        {message.callStatus === 'missed' && 'Missed '}
                                        {message.callType === 'video' ? 'Video Call' : 'Voice Call'}
                                    </span>

                                    {/* Show status for other calls */}
                                    {/* {message.callStatus && (
                                        <span className="text-xs opacity-75">
                                            {message.callStatus === 'missed' ? message.senderId === currentUserId ? 'Voice call' : 'You missed a call' :
                                                null
                                            }
                                        </span>
                                    )} */}
                                    <p className={`text-xs mt-1 ${isFromCurrentUser ? "text-gray-300" : "text-gray-500"
                                        } text-right`}>
                                        {formatTime(message.timestamp)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}

                {/* Custom content (polls, etc.) */}
                {renderCustomContent && renderCustomContent(message)}

                {/* Unified Reactions positioned at bottom edge of bubble */}
                {message.reactions && Object.keys(message.reactions).length > 0 && (
                    <MessageReactions
                        message={message}
                        currentUserId={currentUserId}
                        onToggleReaction={onToggleReaction}
                    />
                )}

                {/* Video Modal */}
                {selectedVideo && (
                    <VideoModal
                        videoUrl={selectedVideo}
                        onClose={() => setSelectedVideo(null)}
                    />
                )}

                {/* Viewers Modal */}
                <ViewUsersModal
                    isOpen={viewersModalOpen}
                    onClose={() => setViewersModalOpen(false)}
                    users={viewersList}
                    type="members"
                />
            </div>
        </div>
    );
};

export default React.memo(MessageBubble);